/**
 * Centralized API Configuration
 * All API URLs and configuration should be managed here
 */

/**
 * Resolve backend base URL (scheme + host + port), no trailing slash.
 * Invalid or empty REACT_APP_API_URL falls back to https://servermain.sisrevolution.com so axios does not hit the React dev server (3000) and get 404.
 */
export const getApiBaseUrl = () => {
    const raw = process.env.REACT_APP_API_URL;
    if (raw != null && String(raw).trim() !== '') {
        let u = String(raw).trim().replace(/\/$/, '');
        if (!/^https?:\/\//i.test(u)) {
            u = `http://${u}`;
        }
        try {
            const parsed = new URL(u);
            if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
                const isReactDevPort =
                    process.env.NODE_ENV === 'development' &&
                    (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') &&
                    parsed.port === '3000';
                if (isReactDevPort) {
                    console.warn(
                        '[api] REACT_APP_API_URL points at the React dev server (port 3000); API calls return 404. Using https://servermain.sisrevolution.com for the API server instead.'
                    );
                    return 'https://servermain.sisrevolution.com';
                }
                return u.replace(/\/$/, '');
            }
        } catch (_) {
            /* fall through */
        }
        console.warn('[api] Invalid REACT_APP_API_URL; using https://servermain.sisrevolution.com');
    }
    return 'https://servermain.sisrevolution.com';
};

export const API_CONFIG = {
    BASE_URL: getApiBaseUrl(),
    TIMEOUT: 180000, // 180 seconds (3 minutes) - increased for better reliability
    UPLOAD_TIMEOUT: 300000, // 300 seconds (5 minutes) for file uploads
    
    // API Endpoints
    ENDPOINTS: {
        AUTH: {
            LOGIN: '/api/schools/login',
            REFRESH: '/api/schools/refresh',
            ME: '/api/schools/me',
            LOGOUT: '/api/schools/logout'
        },
        SCHOOLS: {
            GET_DATA: '/api/schools/getschooldata/',
            UPDATE: '/api/schools/update/',
            ONBOARDING_INITIALIZE: '/api/schools/onboarding-initialize',
            ONBOARDING_VERIFY: '/api/schools/onboarding-verify',
            SMS_WALLET: '/api/schools/sms-wallet',
            SMS_TOKENS_INITIALIZE: '/api/schools/sms-tokens-initialize',
            SMS_TOKENS_VERIFY: '/api/schools/sms-tokens-verify',
            SET_SUBSCRIPTION_PLAN: '/api/schools/set-subscription-plan'
        },
        ADMISSIONS: {
            LIST: '/api/admissions/list',
            DETAIL: '/api/admissions/detail',
            REVIEW: '/api/admissions/review',
            ENROLL: '/api/admissions/enroll',
            SETTINGS: '/api/admissions/admin/settings',
            CLASS_FEES: '/api/admissions/admin/class-fees',
            CLASS_FEE: '/api/admissions/admin/class-fee',
            PUBLIC_CONFIG: '/api/admissions/public/config',
            PUBLIC_START: '/api/admissions/public/start',
            PUBLIC_VERIFY: '/api/admissions/public/paystack-verify',
        },
        STUDENTS: {
            GET_DATA: '/api/students/getstudentdata/',
            REGISTER: '/api/students/registerstudent',
            PAY_FEES: '/api/students/payfees/',
            DISCOUNT: '/api/students/discount/',
            ADD_DEBT: '/api/students/adddebt/',
            UPDATE_IMAGE: '/api/students/updateimage/',
            PAY_CHARGES: '/api/students/paycharges/',
            DAILY_CHARGES: '/api/students/dailycharges/',
            WEEKLY_CHARGES: '/api/students/weeklycharges/',
            MONTHLY_CHARGES: '/api/students/monthlycharges/',
            DEBTORS_SMS: '/api/students/debtorssms/',
            BULK_BILL: '/api/students/bulkbill/'
        },
        SCHOOL: {
            UPDATE_SCHOOL_DATA: '/api/school/updateschooldata/',
            EDIT_SCHOOL: '/api/school/editschool/',
            ADD_SCHOOL: '/api/school/addschool/',
            DELETE_SCHOOL: '/api/school/deleteschool/'
        },
        CLASS: {
            GET_DATA: '/api/class/getclassdata1/',
            CLASS_FEES: '/api/class/classfees/'
        },
        CLASS: {
            GET_DATA: '/api/class/getclassdata1/'
        },
        TEACHERS: {
            GET_DATA: '/api/teachers/getteacherdata/'
        },
        PARENTS: {
            GET_DATA: '/api/parents/getparentdata/'
        }
    }
};

/**
 * Build full API URL
 */
export const buildApiUrl = (endpoint) => {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const base = getApiBaseUrl().replace(/\/$/, '');
    return `${base}${cleanEndpoint}`;
};

/**
 * GET (or other) fetch against the API base, then parse JSON safely.
 * Prevents SyntaxError when a misconfigured base URL returns the SPA index.html (starts with <!DOCTYPE).
 */
export async function fetchJsonFromApi(pathOrUrl, options = {}) {
    const pathOnly =
        typeof pathOrUrl === 'string' && !/^https?:\/\//i.test(pathOrUrl)
            ? (pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`)
            : null;
    const url = /^https?:\/\//i.test(pathOrUrl) ? pathOrUrl : buildApiUrl(pathOrUrl);
    const fetchOpts = {
        credentials: 'include',
        ...options
    };
    let res;
    try {
        res = await fetch(url, fetchOpts);
    } catch (e) {
        if (
            pathOnly &&
            e &&
            (e.name === 'TypeError' || /network|fetch|load failed/i.test(String(e.message || '')))
        ) {
            res = await fetch(pathOnly, fetchOpts);
        } else {
            throw e;
        }
    }
    const text = await res.text();
    let data = {};
    if (text) {
        try {
            data = JSON.parse(text);
        } catch {
            const t = text.trim();
            const looksLikeHtml =
                t.startsWith('<!DOCTYPE') ||
                t.startsWith('<!doctype') ||
                t.startsWith('<html');
            const msg = looksLikeHtml
                ? 'API returned a web page instead of JSON. Set REACT_APP_API_URL to your backend (e.g. https://servermain.sisrevolution.com), ensure the API is running, and restart the dev server.'
                : `Invalid JSON from server: ${t.slice(0, 120)}`;
            const err = new Error(msg);
            err.status = res.status;
            throw err;
        }
    }
    if (!res.ok) {
        const msg =
            (data && (data.message || data.error)) || `HTTP ${res.status}`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
    }
    return data;
}

/**
 * Validate API URL format
 */
export const validateApiUrl = (url) => {
    try {
        const urlObj = new URL(url);
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
        return false;
    }
};

// Validate on module load
if (!validateApiUrl(API_CONFIG.BASE_URL)) {
    console.warn(`⚠️ Invalid API URL format: ${API_CONFIG.BASE_URL}`);
}

export default API_CONFIG;

